// SESSION-TIMELINE (A19): the snapshots strip under the Live workspace. Every canvas state of the
// session next to what was said then; click a snapshot (or use the arrow keys) to scrub back to
// it, read only: the preview names the blocks and what was said, it never changes the live
// canvas. Below it: the session's files, v1/v2 of a block shown twice, and a feedback box that
// posts a comment on the snapshot's card (or the session thread). Everything comes from the
// server already cut to what this viewer may read.
import { Button, cn } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Api } from "../../../api/client";
import type { CompanyAccess } from "../../company";
import { layoutRects } from "../desk-layout";
import {
  type BlockVersions,
  compareVersions,
  neighbours,
  type SessionTimeline,
  type Snapshot,
  snapshotAtIndex,
  versionsAt,
} from "./timeline-model";
import { sendFeedback, timelineQuery } from "./timeline-queries";

const time = (iso: string) => iso.slice(11, 19);

export function SessionStrip({
  api,
  company,
  sessionId,
}: {
  api: Api;
  company: CompanyAccess;
  sessionId: string;
}) {
  const q = useQuery({ ...timelineQuery(api, company.id, sessionId), refetchInterval: 15_000 });
  if (!q.data || q.data.snapshots.length === 0) return null;
  return <Strip api={api} co={company.id} sessionId={sessionId} t={q.data} />;
}

function Strip({
  api,
  co,
  sessionId,
  t,
}: {
  api: Api;
  co: string;
  sessionId: string;
  t: SessionTimeline;
}) {
  // null: follow the newest snapshot; a number: scrubbed back to that one.
  const [at, setAt] = useState<number | null>(null);
  const snap = snapshotAtIndex(t, at);
  if (!snap) return null;
  const nb = neighbours(t, snap.index);
  const scrubbed = at !== null && snap.index < t.snapshots.length - 1;
  return (
    <section
      data-slot="session-timeline"
      aria-label="Session timeline"
      className="flex min-w-0 flex-col gap-3 border-t border-border px-6 py-4 max-md:px-4"
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft" && nb.prev !== null) setAt(nb.prev);
        if (e.key === "ArrowRight" && nb.next !== null) setAt(nb.next);
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-medium" data-slot="timeline-heading">
          {scrubbed ? `Viewing ${time(snap.at)}` : "Session timeline"}
        </h2>
        {scrubbed ? (
          <Button size="sm" variant="ghost" onClick={() => setAt(null)}>
            Back to now
          </Button>
        ) : null}
      </div>
      <ol
        data-slot="timeline-strip"
        className="flex gap-2 overflow-x-auto pb-1"
        aria-label="Snapshots"
      >
        {t.snapshots.map((s) => (
          <li key={s.op_id} className="shrink-0">
            <button
              type="button"
              data-snapshot={s.index}
              aria-current={s.index === snap.index ? "true" : undefined}
              onClick={() => setAt(s.index === t.snapshots.length - 1 ? null : s.index)}
              className={cn(
                "flex w-40 flex-col gap-0.5 rounded-md border border-border p-2 text-left text-xs",
                s.index === snap.index && "border-primary bg-accent",
              )}
            >
              <MiniDesk s={s} />
              <span className="text-muted-foreground">
                {time(s.at)} · {s.blocks.length} window{s.blocks.length === 1 ? "" : "s"}
              </span>
              <span className="line-clamp-2">{s.summary}</span>
              {s.said.length > 0 ? (
                <span className="text-muted-foreground">{s.said.length} said</span>
              ) : null}
            </button>
          </li>
        ))}
      </ol>
      <SnapshotView t={t} s={snap} scrubbed={scrubbed} />
      <Feedback api={api} co={co} sessionId={sessionId} s={snap} />
      <Versions t={t} />
      <Files t={t} s={snap} />
    </section>
  );
}

/**
 * LIVE-DESKTOP: a snapshot's desk in miniature (24 x 16 units, SVG): its windows where they were,
 * the pointed-at one in the primary colour; windows never placed are tiled; docked ones left out.
 */
function MiniDesk({ s }: { s: Snapshot }) {
  const geo = s.windows ?? {};
  const open = s.blocks.filter((b) => geo[b.id]?.state !== "docked");
  const unplaced = open.filter((b) => !geo[b.id] || (geo[b.id]?.w ?? 0) === 0);
  const auto = layoutRects("tile", unplaced.length);
  const rects = open
    .map((b) => {
      const g = geo[b.id];
      const r =
        g && g.w > 0
          ? g.state === "max"
            ? { x: 0, y: 0, w: 24, h: 16 }
            : g
          : auto[unplaced.indexOf(b)];
      return r ? { id: b.id, z: g?.z ?? 0, ...r } : null;
    })
    .filter((r): r is NonNullable<typeof r> => r !== null)
    .sort((a, b) => a.z - b.z);
  return (
    <svg
      viewBox="0 0 24 16"
      aria-hidden="true"
      data-slot="mini-desk"
      className="h-12 w-full rounded-sm bg-muted"
    >
      {rects.map((r) => (
        <rect
          key={r.id}
          x={r.x + 0.25}
          y={r.y + 0.25}
          width={Math.max(0.5, r.w - 0.5)}
          height={Math.max(0.5, r.h - 0.5)}
          rx={0.5}
          strokeWidth={0.2}
          className={cn(
            s.highlight?.block_id === r.id
              ? "fill-primary-soft stroke-primary"
              : "fill-card stroke-border",
          )}
        />
      ))}
    </svg>
  );
}

function SnapshotView({ t, s, scrubbed }: { t: SessionTimeline; s: Snapshot; scrubbed: boolean }) {
  const vs = versionsAt(t, s);
  return (
    <div data-slot="timeline-snapshot" className="grid gap-3 md:grid-cols-2">
      <div className="rounded-md border border-border p-3">
        <p className="mb-2 text-xs text-muted-foreground">
          {scrubbed ? `Read-only view at ${time(s.at)}` : `Now, ${time(s.at)}`} · {s.layout} layout
        </p>
        {s.blocks.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing was on the canvas.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {s.blocks.map((b) => {
              const v = vs.find((x) => x.blockId === b.id);
              return (
                <li
                  key={b.id}
                  data-block={b.id}
                  className={cn(
                    "rounded border border-border px-2 py-1 text-sm",
                    s.highlight?.block_id === b.id && "ring-2 ring-primary",
                  )}
                >
                  {b.title ?? b.kind.replace("_", " ")}
                  {v ? (
                    <span className="ml-1 text-xs text-muted-foreground">v{v.version}</span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {s.highlight?.note ? <p className="mt-2 text-sm">{s.highlight.note}</p> : null}
      </div>
      <div className="rounded-md border border-border p-3">
        <p className="mb-2 text-xs text-muted-foreground">Said at this moment</p>
        {s.said.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing was said.</p>
        ) : (
          <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto text-sm">
            {s.said.map((l) => (
              <li key={l.message_id}>
                <span className="text-xs text-muted-foreground">
                  {time(l.at)} {l.author_kind}
                </span>{" "}
                {l.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Feedback({
  api,
  co,
  sessionId,
  s,
}: {
  api: Api;
  co: string;
  sessionId: string;
  s: Snapshot;
}) {
  const [text, setText] = useState("");
  const qc = useQueryClient();
  const send = useMutation({
    mutationFn: () => sendFeedback(api, co, sessionId, s, text),
    onSuccess: ({ threadId }) => {
      setText("");
      void qc.invalidateQueries({ queryKey: ["company", co] });
      void threadId;
    },
  });
  if (!s.target) return null;
  return (
    <form
      data-slot="timeline-feedback"
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) send.mutate();
      }}
    >
      <label className="text-xs text-muted-foreground" htmlFor="timeline-feedback-text">
        Feedback on this snapshot, as a comment on{" "}
        {s.target.kind === "task" ? "its card" : "the session thread"}
      </label>
      <textarea
        id="timeline-feedback-text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        className="rounded-md border border-border bg-background p-2 text-sm"
      />
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!text.trim() || send.isPending}>
          Comment
        </Button>
        {send.isSuccess ? (
          <span className="text-xs text-muted-foreground">Comment posted.</span>
        ) : null}
        {send.isError ? (
          <span className="text-xs text-destructive">Could not post the comment.</span>
        ) : null}
      </div>
    </form>
  );
}

function Versions({ t }: { t: SessionTimeline }) {
  if (t.versions.length === 0) return null;
  return (
    <div data-slot="timeline-versions" className="flex flex-col gap-2">
      <h3 className="text-xs font-medium text-muted-foreground">Shown more than once</h3>
      {t.versions.map((bv) => (
        <VersionPair key={bv.block_id} bv={bv} />
      ))}
    </div>
  );
}

function VersionPair({ bv }: { bv: BlockVersions }) {
  const [a, b] = [bv.versions.at(-2), bv.versions.at(-1)];
  if (!a || !b) return null;
  const d = compareVersions(a, b);
  return (
    <p className="text-sm" data-version-block={bv.block_id}>
      {b.block.title ?? bv.block_id}: v{a.version} at {time(a.at)} against v{b.version} at{" "}
      {time(b.at)}.{" "}
      {d.same
        ? "Shown again unchanged."
        : [
            d.changed.length ? `Changed ${d.changed.join(", ")}.` : "",
            d.addedFiles.length ? `${d.addedFiles.length} file(s) added.` : "",
            d.removedFiles.length ? `${d.removedFiles.length} file(s) removed.` : "",
          ]
            .filter(Boolean)
            .join(" ")}
    </p>
  );
}

function Files({ t, s }: { t: SessionTimeline; s: Snapshot }) {
  if (t.files.length === 0) return null;
  return (
    <div data-slot="timeline-files" className="flex flex-col gap-1">
      <h3 className="text-xs font-medium text-muted-foreground">Files shown in the session</h3>
      <ul className="text-sm">
        {t.files.map((f) => (
          <li key={f.id} className={cn(f.snapshot_index > s.index && "opacity-50")}>
            {f.name} <span className="text-xs text-muted-foreground">{time(f.at)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
